<template>
  <Dialog :open="isOpen" @update:open="$emit('update:isOpen', $event)">
    <DialogContent class="sm:max-w-screen-xl">
      <DialogHeader>
        <DialogTitle class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <img 
              v-if="localCompany?.logo_url" 
              :src="localCompany.logo_url" 
              :alt="localCompany?.name"
              class="w-full h-full object-contain rounded-lg"
            >
            <Building v-else class="w-5 h-5 text-gray-500" />
          </div>
          <span>{{ localCompany?.name }}</span>
        </DialogTitle>
      </DialogHeader>

      <div class="space-y-6 py-4">
        <!-- Company Stats -->
        <div class="grid grid-cols-2 gap-4">
          <div class="bg-gray-50 p-4 rounded-lg">
            <p class="text-sm text-gray-500">Total Referrers</p>
            <p class="text-2xl font-semibold">{{ localCompany?.referring_people.length || 0 }}</p>
          </div>
          <div class="bg-gray-50 p-4 rounded-lg">
            <p class="text-sm text-gray-500">Active Referrers</p>
            <p class="text-2xl font-semibold">
              {{ localCompany?.referring_people.filter(r => r.is_active).length || 0 }}
            </p>
          </div>
        </div>

        <!-- Staff Notes -->
        <div class="space-y-2">
          <div class="flex items-center space-x-2 text-sm text-gray-500">
            <NotebookPen class="w-4 h-4" />
            <span>Staff Notes</span>
          </div>
          
          <div v-if="isEditingNotes">
            <textarea
              v-model="draftNotes"
              :disabled="isSavingNotes"
              class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              rows="4"
              placeholder="Add internal notes..."
              @blur="saveNotes"
              @keydown.enter.ctrl="saveNotes"
              @keydown.escape="cancelEditingNotes"
              ref="notesTextarea"
            ></textarea>
            <div class="flex justify-end space-x-2 mt-2">
              <Button 
                variant="outline" 
                size="sm"
                :disabled="isSavingNotes"
                @click="cancelEditingNotes"
              >
                Cancel
              </Button>
              <Button 
                size="sm"
                :disabled="isSavingNotes"
                @click="saveNotes"
              >
                <div v-if="isSavingNotes" class="flex items-center">
                  <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </div>
                <span v-else>Save</span>
              </Button>
            </div>
          </div>
          <div 
            v-else 
            class="text-sm text-gray-600 whitespace-pre-wrap p-3 bg-gray-50 rounded-lg min-h-[4rem] cursor-text hover:bg-gray-100 transition-colors"
            @click="startEditingNotes"
          >
            {{ localCompany?.outtalent_internal_notes || 'Click to add staff notes...' }}
          </div>
        </div>

        <!-- Default Referrer -->
        <div class="space-y-2">
          <div class="flex items-center space-x-2 text-sm text-gray-500">
            <CheckCircle class="w-4 h-4" />
            <span>Default Referrer</span>
          </div>
          <div class="relative">
            <Select
              :model-value="localCompany?.default_referrer ? localCompany.default_referrer.toString() : 'none'"
              :disabled="isUpdatingDefaultReferrer"
              @update:model-value="updateDefaultReferrer"
            >
              <SelectTrigger class="w-full">
                <SelectValue :placeholder="getDefaultReferrerName" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem 
                  v-for="referrer in localCompany?.referring_people" 
                  :key="referrer.id"
                  :value="referrer.id.toString()"
                >
                  {{ referrer.name }}
                </SelectItem>
              </SelectContent>
            </Select>
            <div 
              v-if="isUpdatingDefaultReferrer" 
              class="absolute inset-0 flex items-center justify-center bg-white/80 rounded-md"
            >
              <div class="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Custom Close Button -->
      <button
        @click="$emit('update:isOpen', false)"
        class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
      >
        <X class="w-4 h-4" />
        <span class="sr-only">Close</span>
      </button>
    </DialogContent>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { Building, NotebookPen, CheckCircle, X } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { useReferrersStore } from '@/stores/referrers'
import { useEmployerStore } from '@/stores/employer'

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  company: {
    type: Object,
    default: null
  }
})

defineEmits(['update:isOpen'])

const referrersStore = useReferrersStore()
const employerStore = useEmployerStore()

const isEditingNotes = ref(false)
const draftNotes = ref('')
const isSavingNotes = ref(false)
const isUpdatingDefaultReferrer = ref(false)
const notesTextarea = ref(null)

// Local reactive company data that gets updated from the store
const localCompany = ref(null)

// Watch for changes in the company prop and update local data
watch(() => props.company, (newCompany) => {
  if (newCompany) {
    localCompany.value = { ...newCompany }
  } else {
    localCompany.value = null
  }
}, { immediate: true })

// Watch for changes in the referrers store data and update local company if it matches
watch(() => referrersStore.referrersGroupedByCompany, (newData) => {
  if (localCompany.value && newData.length > 0) {
    const updatedCompany = newData.find(company => company.id === localCompany.value.id)
    if (updatedCompany) {
      localCompany.value = { ...updatedCompany }
    }
  }
}, { deep: true })

// Computed property for default referrer name
const getDefaultReferrerName = computed(() => {
  if (!localCompany.value?.default_referrer || !localCompany.value?.referring_people) return 'None'
  const defaultReferrer = localCompany.value.referring_people.find(
    r => r.id === localCompany.value.default_referrer
  )
  return defaultReferrer ? defaultReferrer.name : 'None'
})

// Start editing notes
const startEditingNotes = async () => {
  draftNotes.value = localCompany.value?.outtalent_internal_notes || ''
  isEditingNotes.value = true
  // Auto-focus the textarea after it's rendered
  await nextTick()
  if (notesTextarea.value) {
    notesTextarea.value.focus()
  }
}

// Cancel editing notes
const cancelEditingNotes = () => {
  draftNotes.value = ''
  isEditingNotes.value = false
}

// Save notes
const saveNotes = async () => {
  if (!localCompany.value || isSavingNotes.value) return
  
  isSavingNotes.value = true
  try {
    await employerStore.updateEmployerNotes(localCompany.value.id, draftNotes.value)
    // Update local company data immediately
    localCompany.value.outtalent_internal_notes = draftNotes.value
    // Refresh the referrers data to get updated notes
    await referrersStore.fetchReferrersGroupedByCompany(true)
    isEditingNotes.value = false
  } catch (error) {
    console.error('Failed to save notes:', error)
  } finally {
    isSavingNotes.value = false
  }
}

// Update default referrer
const updateDefaultReferrer = async (referrerId) => {
  if (!localCompany.value || isUpdatingDefaultReferrer.value) return
  
  isUpdatingDefaultReferrer.value = true
  try {
    await referrersStore.setDefaultReferrer(
      localCompany.value.id, 
      referrerId && referrerId !== 'none' ? Number(referrerId) : null
    )
    // Update local company data immediately
    localCompany.value.default_referrer = referrerId && referrerId !== 'none' ? Number(referrerId) : undefined
  } catch (error) {
    console.error('Failed to update default referrer:', error)
  } finally {
    isUpdatingDefaultReferrer.value = false
  }
}
</script> 